// Loading.js
import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { LeftSide } from '../../components/LeftSide/LeftSide';
import { Navi } from '../../components/Navi/Navi';
import { RightBar } from '../../components/RightBar/RightBar';
import { toKST } from '../../utils/date';
import '../../styles/common.css'; // 공통 CSS 파일 import
import '../Users/User.css';
import AuthService from '../../services/authService';
import api from '../../utils/api'; // 새로운 API 인스턴스 불러오기
import { toast } from 'react-toastify';
import { useMinLoadingDisplay } from '../../hooks/useMinLoadingDisplay';

const Admins = ({ title = '관리자관리' }) => {
    // 회원 관리 관련 상태
    const [admins, setAdmins] = useState([]);
    const [filteredAdmins, setFilteredAdmins] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    const [sortOrder, setSortOrder] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const showListLoading = useMinLoadingDisplay(isLoading);
    const [adminId, setAdminId] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [adminName, setAdminName] = useState('');
    const [adminRole, setAdminRole] = useState('admin');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);

    // 관리자 데이터 가져오기
    const fetchAdmins = async (
        page = currentPage,
        search = searchTerm,
        status = statusFilter,
        sort = sortOrder
    ) => {
        setIsLoading(true);
        try {
            const response = await api.get('/admins', {
                params: {
                    page: page,
                    limit: itemsPerPage,
                    search: search,
                    status: status,
                    sort: sort,
                },
            });

            if (response.data.success) {
                setAdmins(response.data.admins);
                setFilteredAdmins(response.data.admins);
                setTotalPages(Math.ceil(response.data.total / itemsPerPage));
            } else {
                toast.error(
                    response.data.message || '관리자 목록을 불러오지 못했습니다.'
                );
                console.error('데이터를 불러오지 못했습니다:', response.data.message);
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                    err?.message ||
                    '관리자 목록을 불러오는 중 오류가 발생했습니다.'
            );
            console.error('사용자 데이터 가져오기 실패:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // 관리자 추가 함수
    const addAdmin = async () => {
        // 입력값 유효성 검사
        if (!adminId || !adminPassword || !adminName) {
            toast.warning('모든 필수 항목을 입력해주세요.');
            return false;
        }

        if (adminId.length < 4) {
            toast.warning('아이디는 4자 이상이어야 합니다.');
            return false;
        }

        if (adminPassword.length < 6) {
            toast.warning('비밀번호는 6자 이상이어야 합니다.');
            return false;
        }

        const adminData = {
            adminId,
            adminPassword,
            adminName,
            adminRole,
        };
        try {
            const response = await api.post('/admins', adminData);
            if (response.data.success) {
                toast.success('관리자가 추가되었습니다.');
                fetchAdmins();
                // 입력값 초기화
                setAdminId('');
                setAdminPassword('');
                setAdminName('');
                setAdminRole('admin');
                // 모달 닫기
                setShowAddModal(false);
                return true;
            }
            toast.error(response.data?.message || '관리자 추가에 실패했습니다.');
            return false;
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error('관리자 추가 중 오류가 발생했습니다.');
            }
            console.error('관리자 추가 실패:', err);
            return false;
        }
    };

    // 관리자 삭제 함수
    const deleteAdmin = async adminId => {
        try {
            const response = await api.delete(`/admins/${adminId}`);
            if (response.data.success) {
                toast.success('관리자가 삭제되었습니다.');
                fetchAdmins();
                return true;
            }
            toast.error(response.data?.message || '삭제에 실패했습니다.');
            return false;
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error('관리자 삭제 중 오류가 발생했습니다.');
            }
            console.error('관리자 삭제 실패:', err);
            return false;
        }
    };

    // 관리자 복구 함수
    const restoreAdmin = async adminId => {
        try {
            const response = await api.post(`/admins/${adminId}/restore`);
            if (response.data.success) {
                toast.success('관리자가 복구되었습니다.');
                fetchAdmins();
                return true;
            }
            toast.error(response.data?.message || '복구에 실패했습니다.');
            return false;
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error('관리자 복구 중 오류가 발생했습니다.');
            }
            console.error('관리자 복구 실패:', err);
            return false;
        }
    };

    // 관리자 수정 함수
    const editAdmin = async () => {
        if (!editingAdmin) return;

        // 입력값 유효성 검사
        if (!editingAdmin.adminName) {
            toast.warning('관리자명을 입력해주세요.');
            return false;
        }

        try {
            const response = await api.put(`/admins/${editingAdmin.id}`, {
                adminName: editingAdmin.adminName,
                adminPassword: editingAdmin.adminPassword,
                adminRole: editingAdmin.adminRole,
            });

            if (response.data.success) {
                toast.success('관리자 정보가 수정되었습니다.');
                fetchAdmins();
                setShowEditModal(false);
                return true;
            }
            toast.error(response.data?.message || '관리자 수정에 실패했습니다.');
            return false;
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error('관리자 수정 중 오류가 발생했습니다.');
            }
            console.error('관리자 수정 실패:', err);
            return false;
        }
    };

    // 관리자 수정 모달 열기
    const handleEditClick = admin => {
        setEditingAdmin({
            id: admin.id,
            adminId: admin.admin_id,
            adminName: admin.name,
            adminRole: admin.role,
        });
        setShowEditModal(true);
    };

    // 현재 페이지의 사용자 데이터
    const getCurrentAdmins = () => {
        return filteredAdmins;
    };

    // 페이지 변경 핸들러
    const handlePageChange = pageNumber => {
        setCurrentPage(pageNumber);
    };

    // 검색어 변경 핸들러
    const handleSearch = e => {
        const value = e.target.value;
        setSearchTerm(value);
        // 검색 버튼 클릭 시에만 검색되도록 applyFilters 호출 제거
    };

    // 상태 필터 변경 핸들러
    const handleStatusFilter = e => {
        const value = e.target.value;
        setStatusFilter(value);
        setCurrentPage(1);

        let statusValue = '';
        if (value === 'active') statusValue = '사용중';
        else if (value === 'deleted') statusValue = '삭제됨';

        // 검색 버튼 클릭 시에만 검색되도록 applyFilters 호출 제거
    };

    // 검색 조건 초기화 함수 추가
    const resetSearch = () => {
        setSearchTerm('');
        setStatusFilter('active');
        setSortOrder('');
        setCurrentPage(1);
        fetchAdmins(1, '', 'active', ''); // 초기화된 값으로 직접 호출
    };

    // 엔터 키 검색 핸들러 추가
    const handleKeyPress = e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setCurrentPage(1); // 검색 시 첫 페이지로 이동
            fetchAdmins(1, searchTerm, statusFilter, sortOrder);
        }
    };

    useEffect(() => {
        // 컴포넌트 마운트 시와 페이지 변경 시에만 사용자 데이터 가져오기
        fetchAdmins();
    }, [currentPage]);

    // 페이지네이션 렌더링
    const renderPagination = () => {
        const pageNumbers = [];

        // 페이지 번호 계산 (최대 10개 표시)
        let startPage = Math.max(1, currentPage - 5);
        let endPage = Math.min(totalPages, startPage + 9);

        if (endPage - startPage < 9 && totalPages > 10) {
            startPage = Math.max(1, endPage - 9);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <nav aria-label="회원 목록 페이지네이션">
                <ul className="pagination pagination-sm justify-content-center mt-3">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <Link
                            className="page-link"
                            onClick={e => {
                                e.preventDefault();
                                handlePageChange(1);
                            }}
                            title="맨 앞"
                        >
                            <i className="fas fa-angle-double-left"></i>
                        </Link>
                    </li>

                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <Link
                            className="page-link"
                            onClick={e => {
                                e.preventDefault();
                                if (currentPage > 1) handlePageChange(currentPage - 1);
                            }}
                        >
                            이전
                        </Link>
                    </li>

                    {pageNumbers.map(number => (
                        <li
                            key={number}
                            className={`page-item ${currentPage === number ? 'active' : ''}`}
                        >
                            <Link
                                className="page-link"
                                onClick={e => {
                                    e.preventDefault();
                                    handlePageChange(number);
                                }}
                            >
                                {number}
                            </Link>
                        </li>
                    ))}

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <Link
                            className="page-link"
                            onClick={e => {
                                e.preventDefault();
                                if (currentPage < totalPages) handlePageChange(currentPage + 1);
                            }}
                        >
                            다음
                        </Link>
                    </li>

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <Link
                            className="page-link"
                            onClick={e => {
                                e.preventDefault();
                                handlePageChange(totalPages);
                            }}
                            title="맨 뒤"
                        >
                            <i className="fas fa-angle-double-right"></i>
                        </Link>
                    </li>
                </ul>
            </nav>
        );
    };

    return (
        <>
            {/* <!-- Begin page --> */}
            <div id="wrapper">
                <Navi title={title} />

                <LeftSide />

                <div className="content-page">
                    <div className="content">
                        {/* <!-- Start Content--> */}
                        <div className="container-fluid users-admin-page">
                            <div className="row">
                                <div className="col-xl-12">
                                    <div className="card">
                                        <div className="card-body">
                                            {/* 검색 및 필터링 영역 */}
                                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-3">
                                                {/* 왼쪽: 필터, 정렬, 검색 */}
                                                <div className="d-flex flex-wrap align-items-center gap-1">
                                                    <select
                                                        className="form-select form-select-sm"
                                                        aria-label="상태 필터"
                                                        value={statusFilter}
                                                        onChange={handleStatusFilter}
                                                        style={{ width: '160px' }}
                                                    >
                                                        <option value="active">사용중</option>
                                                        <option value="deleted">삭제됨</option>
                                                        <option value="">모든 상태</option>
                                                    </select>

                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="관리자 아이디 or 관리자명 검색"
                                                        value={searchTerm}
                                                        onChange={handleSearch}
                                                        onKeyPress={handleKeyPress}
                                                        style={{ width: '200px' }}
                                                    />

                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        type="button"
                                                        onClick={() => {
                                                            setCurrentPage(1); // 검색 시 첫 페이지로 이동
                                                            fetchAdmins(
                                                                1,
                                                                searchTerm,
                                                                statusFilter,
                                                                sortOrder
                                                            ); // 현재 상태 값으로 직접 호출
                                                        }}
                                                    >
                                                        <i className="fas fa-search"></i> 검색
                                                    </button>
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        type="button"
                                                        onClick={resetSearch}
                                                    >
                                                        <i className="fas fa-sync-alt"></i> 초기화
                                                    </button>
                                                </div>

                                                {/* 오른쪽: 관리자 추가 */}
                                                <div>
                                                    <button
                                                        className="btn btn-success btn-sm"
                                                        onClick={() => setShowAddModal(true)}
                                                    >
                                                        <i className="fas fa-user-plus"></i> 관리자
                                                        추가
                                                    </button>
                                                </div>
                                            </div>

                                            {/* 관리자 추가 모달 */}
                                            {showAddModal && (
                                                <>
                                                    <div
                                                        className="modal fade show"
                                                        id="addUserModal"
                                                        tabIndex="-1"
                                                        aria-labelledby="addUserModalLabel"
                                                        aria-modal="true"
                                                        role="dialog"
                                                        style={{ display: 'block' }}
                                                    >
                                                        <div className="modal-dialog modal-dialog-centered admin-form-modal">
                                                            <div className="modal-content">
                                                                <div className="modal-header">
                                                                    <h5
                                                                        className="modal-title"
                                                                        id="addUserModalLabel"
                                                                    >
                                                                        신규 관리자 추가
                                                                    </h5>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-close"
                                                                        data-bs-dismiss="modal"
                                                                        aria-label="Close"
                                                                        onClick={() =>
                                                                            setShowAddModal(false)
                                                                        }
                                                                    ></button>
                                                                </div>
                                                                <div className="modal-body">
                                                                    <form autoComplete="off">
                                                                        <div className="mb-3">
                                                                            <label
                                                                                htmlFor="adminId"
                                                                                className="form-label"
                                                                            >
                                                                                아이디
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                id="adminId"
                                                                                autoComplete="off"
                                                                                value={adminId}
                                                                                onChange={e =>
                                                                                    setAdminId(
                                                                                        e.target
                                                                                            .value
                                                                                    )
                                                                                }
                                                                                maxLength={20}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-3">
                                                                            <label
                                                                                htmlFor="adminPassword"
                                                                                className="form-label"
                                                                            >
                                                                                비밀번호
                                                                            </label>
                                                                            <input
                                                                                type="password"
                                                                                className="form-control"
                                                                                id="adminPassword"
                                                                                autoComplete="new-password"
                                                                                value={
                                                                                    adminPassword
                                                                                }
                                                                                onChange={e =>
                                                                                    setAdminPassword(
                                                                                        e.target
                                                                                            .value
                                                                                    )
                                                                                }
                                                                                maxLength={20}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-3">
                                                                            <label
                                                                                htmlFor="adminName"
                                                                                className="form-label"
                                                                            >
                                                                                관리자명
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                id="adminName"
                                                                                autoComplete="off"
                                                                                value={adminName}
                                                                                onChange={e =>
                                                                                    setAdminName(
                                                                                        e.target
                                                                                            .value
                                                                                    )
                                                                                }
                                                                                maxLength={20}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-3">
                                                                            <label
                                                                                htmlFor="adminRole"
                                                                                className="form-label"
                                                                            >
                                                                                등급
                                                                            </label>
                                                                            <select
                                                                                className="form-select"
                                                                                id="adminRole"
                                                                                value={adminRole}
                                                                                onChange={e =>
                                                                                    setAdminRole(
                                                                                        e.target
                                                                                            .value
                                                                                    )
                                                                                }
                                                                            >
                                                                                <option value="admin">
                                                                                    admin
                                                                                </option>
                                                                            </select>
                                                                        </div>
                                                                    </form>
                                                                </div>
                                                                <div className="modal-footer">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-secondary"
                                                                        onClick={() =>
                                                                            setShowAddModal(false)
                                                                        }
                                                                    >
                                                                        취소
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary"
                                                                        onClick={addAdmin}
                                                                    >
                                                                        저장
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* backdrop 수동 추가 */}
                                                    <div className="modal-backdrop fade show ant-like-modal-backdrop"></div>
                                                </>
                                            )}

                                            {/* 관리자 수정 모달 */}
                                            {showEditModal && (
                                                <>
                                                    <div
                                                        className="modal fade show"
                                                        id="editAdminModal"
                                                        tabIndex="-1"
                                                        aria-labelledby="editAdminModalLabel"
                                                        aria-modal="true"
                                                        role="dialog"
                                                        style={{ display: 'block' }}
                                                    >
                                                        <div className="modal-dialog modal-dialog-centered admin-form-modal">
                                                            <div className="modal-content">
                                                                <div className="modal-header">
                                                                    <h5
                                                                        className="modal-title"
                                                                        id="editAdminModalLabel"
                                                                    >
                                                                        관리자 정보 수정
                                                                    </h5>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-close"
                                                                        data-bs-dismiss="modal"
                                                                        aria-label="Close"
                                                                        onClick={() =>
                                                                            setShowEditModal(false)
                                                                        }
                                                                    ></button>
                                                                </div>
                                                                <div className="modal-body">
                                                                    <form autoComplete="off">
                                                                        <div className="mb-3">
                                                                            <label
                                                                                htmlFor="editAdminId"
                                                                                className="form-label"
                                                                            >
                                                                                아이디
                                                                            </label>
                                                                            <div className="form-control-plaintext">
                                                                                {
                                                                                    editingAdmin.adminId
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                        <div className="mb-3">
                                                                            <label
                                                                                htmlFor="editAdminName"
                                                                                className="form-label"
                                                                            >
                                                                                관리자명
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                id="editAdminName"
                                                                                autoComplete="off"
                                                                                value={
                                                                                    editingAdmin.adminName
                                                                                }
                                                                                onChange={e =>
                                                                                    setEditingAdmin(
                                                                                        {
                                                                                            ...editingAdmin,
                                                                                            adminName:
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                        }
                                                                                    )
                                                                                }
                                                                                maxLength={20}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-3">
                                                                            <label
                                                                                htmlFor="editAdminPassword"
                                                                                className="form-label"
                                                                            >
                                                                                비밀번호 (입력시
                                                                                변경)
                                                                            </label>
                                                                            <input
                                                                                type="password"
                                                                                className="form-control"
                                                                                id="editAdminPassword"
                                                                                autoComplete="off"
                                                                                onChange={e =>
                                                                                    setEditingAdmin(
                                                                                        {
                                                                                            ...editingAdmin,
                                                                                            adminPassword:
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                        }
                                                                                    )
                                                                                }
                                                                                maxLength={20}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-3">
                                                                            <label
                                                                                htmlFor="editAdminRole"
                                                                                className="form-label"
                                                                            >
                                                                                등급
                                                                            </label>
                                                                            <select
                                                                                className="form-select"
                                                                                id="editAdminRole"
                                                                                value={
                                                                                    editingAdmin.adminRole
                                                                                }
                                                                                onChange={e =>
                                                                                    setEditingAdmin(
                                                                                        {
                                                                                            ...editingAdmin,
                                                                                            adminRole:
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                        }
                                                                                    )
                                                                                }
                                                                            >
                                                                                <option value="admin">
                                                                                    admin
                                                                                </option>
                                                                            </select>
                                                                        </div>
                                                                    </form>
                                                                </div>
                                                                <div className="modal-footer">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-secondary"
                                                                        onClick={() =>
                                                                            setShowEditModal(false)
                                                                        }
                                                                    >
                                                                        취소
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary"
                                                                        onClick={editAdmin}
                                                                    >
                                                                        저장
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* backdrop 수동 추가 */}
                                                    <div className="modal-backdrop fade show ant-like-modal-backdrop"></div>
                                                </>
                                            )}

                                            {/* 회원 관리 테이블 */}
                                            <div
                                                className="table-responsive"
                                                style={{ overflowX: 'auto' }}
                                            >
                                                {showListLoading ? (
                                                    <div className="text-center p-4">
                                                        <div
                                                            className="loading-spinner loading-spinner-lg"
                                                            role="status"
                                                            aria-live="polite"
                                                        >
                                                            <span className="visually-hidden">
                                                                데이터를 불러오는 중입니다.
                                                            </span>
                                                        </div>
                                                        <p className="mt-2">
                                                            데이터를 불러오는 중입니다...
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <table className="table table-bordered table-hover table-sm align-middle text-nowrap">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th className="text-center">
                                                                    번호
                                                                </th>
                                                                <th className="text-center">
                                                                    아이디
                                                                </th>
                                                                <th className="text-center">
                                                                    관리자명
                                                                </th>
                                                                <th className="text-center">
                                                                    등급
                                                                </th>
                                                                <th className="text-center">
                                                                    등록일
                                                                </th>
                                                                <th className="text-center">
                                                                    최근접속일
                                                                </th>
                                                                <th className="text-center">
                                                                    상태
                                                                </th>
                                                                <th className="text-center">
                                                                    관리
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {getCurrentAdmins().map(admin => (
                                                                <tr
                                                                    key={admin.id}
                                                                    className={admin.rowClass}
                                                                >
                                                                    <td className="text-center">
                                                                        {admin.id}
                                                                    </td>
                                                                    <td className="text-center users-admin-col-id">
                                                                        {admin.admin_id}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {admin.name}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {admin.role}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {toKST(admin.registerDate)}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {toKST(admin.lastLoginDate)}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span
                                                                            className={`badge ${admin.statusClass} p-2`}
                                                                        >
                                                                            {admin.status}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <button
                                                                            className="btn btn-sm btn-warning me-1"
                                                                            onClick={() =>
                                                                                handleEditClick(
                                                                                    admin
                                                                                )
                                                                            }
                                                                        >
                                                                            <i className="fas fa-edit"></i>
                                                                        </button>
                                                                        {admin.is_deleted ? (
                                                                            <button
                                                                                className="btn btn-sm btn-success"
                                                                                onClick={() => {
                                                                                    if (
                                                                                        window.confirm(
                                                                                            '삭제된 관리자를 복원 하시겠습니까?'
                                                                                        )
                                                                                    ) {
                                                                                        restoreAdmin(
                                                                                            admin.id
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <i className="fas fa-undo"></i>
                                                                            </button>
                                                                        ) : (
                                                                            <button
                                                                                className="btn btn-sm btn-danger"
                                                                                onClick={() => {
                                                                                    if (
                                                                                        window.confirm(
                                                                                            '사용자를 삭제하시겠습니까?'
                                                                                        )
                                                                                    ) {
                                                                                        deleteAdmin(
                                                                                            admin.id
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <i className="fas fa-trash"></i>
                                                                            </button>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>

                                            {/* 페이지네이션 */}
                                            {!showListLoading && renderPagination()}
                                        </div>
                                    </div>
                                </div>
                                {/* <!-- end col --> */}
                            </div>
                            {/* <!-- end row --> */}
                        </div>
                        {/* <!-- container-fluid --> */}
                    </div>
                    {/* <!-- content --> */}
                </div>
            </div>

            <RightBar />

            <div className="rightbar-overlay"></div>
        </>
    );
};

export { Admins };
